import { NIKON_INTERACTIVE_PAGE_DEPENDENCIES } from './nikonInteractiveConstants';
import { INikonInteractiveModel, INikonInteractiveModelProps } from './types';
import { logMessage, logError } from './utils/logging';
import { loadResources } from './utils/resourceLoader';
import { fetchData } from './utils/apiClient';

/**
 * NikonInteractiveModel provides an interface for interacting with Nikon's e-commerce APIs
 * and managing page-specific scripts and dependencies.
 */
export class NikonInteractiveModel implements INikonInteractiveModel {
  private _host: string;
  private _headers: Record<string, string>;
  private _session: string;
  private _extension: string;
  private _breadKey: string;
  private _breadUrl: string;
  private _localization: string[];
  private _currencyCode: string;
  private _localeCode: string;
  private _isInitialized = false;
  private _scriptRegistry: Map<string, Map<string, () => void>>;
  private _currentPath: string | null;
  private _categoriesCache: any = null;

  /**
   * Constructs a new instance of NikonInteractiveModel.
   */
  constructor({ host, session, locale, extension, breadKey, breadUrl }: INikonInteractiveModelProps) {
    this._host = host;
    this._session = session;
    this._localization = locale.split('@');
    this._extension = extension;
    this._breadKey = breadKey;
    this._breadUrl = breadUrl;
    this._localeCode = this._localization[0];
    this._currencyCode = this._localization[1];
    this._scriptRegistry = new Map<string, Map<string, () => void>>();
    this._currentPath = null;

    this._headers = {
      Accept: 'application/json',
      'Frontastic-Locale': this._localeCode,
      'Frontastic-Session': this._session,
      'Commercetools-Frontend-Extension-Version': this._extension,
    };
  }

  // PRIVATE METHODS //////////////////////////////////////

  /**
   * Runs registered scripts for the given path.
   */
  private _runPageScripts = (path: string): void => {
    const pageScripts = this._scriptRegistry.get(path);
    
    if (!pageScripts || pageScripts.size === 0) {
      logMessage(`No scripts to run for path "${path}".`);
      return;
    }
    
    logMessage(`Running ${pageScripts.size} scripts for path "${path}".`);
    
    pageScripts.forEach((script, key) => {
      try {
        logMessage(`Running script "${key}" on page "${path}".`);
        script();
      } catch (error) {
        logError(`Error running script "${key}" on page "${path}"`, error);
      }
    });
  };

  /**
   * Initializes the Nikon jQuery namespace.
   */
  private _initializeNikonJqueryNamespace = (): Promise<void> => {
    return new Promise((resolve) => {
      try {
        if (!window.jQuery) {
          logError('jQuery not available');
          resolve(); // Resolve instead of reject to prevent disruption
          return;
        }
        
        const nikon = (window['nikon'] ||= window.jQuery.noConflict(true));
        nikon.fx.interval = 42; // 24fps
        
        if (!window.nikon) {
          window.nikon = nikon;
        }

        nikon(document).on('unload', function () {
          window.nikon = null;
        });

        nikon.extend({ utils: {} });
        
        if (window.nikon) {
          logMessage('Nikon jQuery namespace initialized');
          resolve();
        } else {
          logError('Failed to initialize Nikon namespace');
          resolve(); // Resolve instead of reject to prevent disruption
        }
      } catch (error) {
        logError('Error initializing Nikon jQuery namespace', error);
        resolve(); // Resolve instead of reject to prevent disruption
      }
    });
  };

  /**
   * Checks if jQuery is loaded and initializes the Nikon namespace.
   */
  private _checkJQueryLoaded = (): void => {
    if (window.jQuery) {
      this._initializeNikonJqueryNamespace()
        .then(() => {
          logMessage('Dependencies Loaded.');
          this._isInitialized = true;
          // LEGACY: this is used to signal that the API is ready for legacy code
          document.dispatchEvent(new CustomEvent('frontasticApiReady'));
        })
        .catch((error) => {
          logError('Failed to initialize Nikon namespace', error);
          setTimeout(this._checkJQueryLoaded, 500);
        });
    } else {
      logMessage('Loading dependencies...');
      this._isInitialized = false;
      setTimeout(this._checkJQueryLoaded, 500);
    }
  };

  // GETTERS //////////////////////////////////////

  /** Indicates whether the model is initialized. */
  get isInitialized(): boolean {
    return this._isInitialized;
  }

  /** Promise resolving when the page is ready with locale and currency codes. */
  get pageIsReady(): Promise<{ localeCode: string; currencyCode: string }> {
    return new Promise((resolve) => {
      const checkReady = () => {
        if (this._isInitialized && this._localeCode && this._currencyCode && !!this._currentPath) {
          resolve({
            localeCode: this._localeCode,
            currencyCode: this._currencyCode,
          });
        } else {
          setTimeout(checkReady, 100);
        }
      };
      
      checkReady();
    });
  }

  /** Promise resolving to the Bread URL. */
  get breadUrl(): Promise<string> {
    return Promise.resolve(this._breadUrl);
  }

  /** Promise resolving to the Bread key. */
  get breadKey(): Promise<string> {
    return Promise.resolve(this._breadKey);
  }

  /** Promise resolving to the currency code. */
  get currencyCode(): Promise<string> {
    return Promise.resolve(this._currencyCode);
  }

  /** Promise resolving to the formatted locale code. */
  get localeCode(): Promise<string> {
    return Promise.resolve(this._localeCode ? this._localeCode.replace('_', '-') : '');
  }

  // PUBLIC METHODS //////////////////////////////////////

  /**
   * Loads page dependencies (JavaScript files).
   */
  loadPageDependencies(): Promise<any> {
    return loadResources(NIKON_INTERACTIVE_PAGE_DEPENDENCIES);
  }



  /**
   * Registers a script to be executed for the current path.
   */
  registerScript(name: string, runner: any): void {
    if (!this._currentPath) {
      logError(`Cannot register script "${name}" because the current path is not set. Please call initialize() first.`);
      return;
    }

    if (!this._scriptRegistry.has(this._currentPath)) {
      this._scriptRegistry.set(this._currentPath, new Map<string, any>());
    }

    const pageScripts = this._scriptRegistry.get(this._currentPath);

    if (!pageScripts) {
      logError(`No registry found for the current path: ${this._currentPath}.`);
      return;
    }

    if (pageScripts.has(name)) {
      logMessage(`Script for "${name}" is already registered on page "${this._currentPath}".`);
      return;
    }

    pageScripts.set(name, runner);
    logMessage(`Registered script "${name}" on page "${this._currentPath}".`);
  }

  /**
   * Initializes the model with the given path.
   */
  initialize(path: string): void {
    this._currentPath = path;

    if (this._isInitialized) {
      this._runPageScripts(path);
      document.dispatchEvent(new CustomEvent('frontasticApiReady'));
      return;
    }

    this._checkJQueryLoaded();
  }

  /** Initializes Bread Payments. */
  handleInitBreadPayments(): void {
    if (!window.BreadPayments) {
      logError('breadPayments not found');
      return;
    }

    try {
      window.BreadPayments.setInitMode('manual');
      window.BreadPayments.setup({
        integrationKey: this._breadKey,
      });
      
      window.BreadPayments.on('INSTALLMENT:APPLICATION_DECISIONED', (installmentResult: any) => {
        logMessage('APPLICATION_DECISIONED::', installmentResult);
      });
      
      window.BreadPayments.on('INSTALLMENT:APPLICATION_CHECKOUT', (installmentResult: any) => {
        logMessage('APPLICATION_CHECKOUT::', installmentResult);
      });
      
      window.BreadPayments.init();
      logMessage('Bread Payments initialized successfully');
    } catch (error) {
      logError('Error initializing Bread Payments', error);
    }
  }

  /** Fetches the current cart. */
  async handleGetCart(): Promise<any> {
    return fetchData(`${this._host}/action/cart/getCart`, this._headers);
  }

  /**
   * Adds multiple items to the cart.
   */
  async handleAddMultipleItemsToCart(skus: string[], callbackName: any): Promise<any> {
    return new Promise((resolve) => {
      if (!skus || skus.length === 0) {
        logError('No SKUs provided');
        resolve({ isError: true, message: 'No SKUs provided', data: null });
        return;
      }
      
      const timeoutId = setTimeout(() => {
        if (callbackName) {
          window.removeEventListener(callbackName, siMultiItemsEventListener);
        }
        logError('Add to cart operation timed out');
        resolve({ isError: true, message: 'Add to cart operation timed out', data: null });
      }, 10000); // 10 second timeout
      
      const siMultiItemsEventListener = (e: any) => {
        clearTimeout(timeoutId);
        
        if (!e.detail.isError) {
          resolve({
            status: 'success',
            details: e.detail
          });
        } else {
          resolve({
            status: 'error',
            isError: true,
            details: e.detail
          });
        }

        if (callbackName) {
          window.removeEventListener(callbackName, siMultiItemsEventListener);
        }
      };

      try {
        if (callbackName) {
          window.addEventListener(callbackName, siMultiItemsEventListener);
        }

        // Dispatch the event
        const event = new CustomEvent('siAddItemsToCart', {
          detail: {
            skus,
            event: callbackName || null,
          },
        });
        window.dispatchEvent(event);
      } catch (error) {
        clearTimeout(timeoutId);
        logError('Error dispatching add to cart event', error);
        resolve({ isError: true, message: 'Error dispatching add to cart event', data: null });
      }
    });
  }

  /**
   * Fetches a product by its SKU.
   */
  async handleGetProductBySku(sku: string, limit = 99, sortString = ''): Promise<any> {
    if (!sku) {
      logError('SKU is required');
      return { isError: true, message: 'SKU is required', data: null };
    }
    return fetchData(`${this._host}/action/product/getProductBySku?sku=${sku}&limit=${limit}${sortString}`, this._headers);
  }

  /**
   * Fetches multiple products by their SKUs.
   */
  async handleGetProductsBySku(skus: string[], limit = 99, sortString = ''): Promise<any> {
    if (!skus || skus.length === 0) {
      logError('At least one SKU is required');
      return { isError: true, message: 'At least one SKU is required', data: null };
    }
    return fetchData(`${this._host}/action/product/query?skus=${skus}&limit=${limit}${sortString}`, this._headers);
  }

  /**
   * Fetches categories with an optional limit.
   */
  async handleGetCategories(limit = 99): Promise<any> {
    // Check if categories are already cached
    if (this._categoriesCache) {
      return this._categoriesCache;
    }
    
    // Fetch categories from the API and cache them
    this._categoriesCache = await fetchData(
      `${this._host}/action/product/queryCategories?limit=${limit}`, 
      this._headers,
      'GET'
    );
    return this._categoriesCache;
  }

  /**
   * Fetches a category by its slug.
   */
  async handleGetCategoryBySlug(slug: string, sortString = ''): Promise<any> {
    if (!slug) {
      logError('Category slug is required');
      return { isError: true, message: 'Category slug is required', data: null };
    }
    return fetchData(`${this._host}/action/product/queryCategories?slug=${slug}${sortString}`, this._headers, 'GET');
  }

  /**
   * Fetches products in a category by its slug.
   */
  async handleGetCategoryProductsBySlug(slug: string, limit = 99, sortString = ''): Promise<any> {
    if (!slug) {
      logError('Slug is empty or undefined.');
      return { isError: true, message: 'Slug is required', data: null };
    }
    
    try {
      // Fetch Category to Get categoryId
      const categoryData = await fetchData(
        `${this._host}/action/product/queryCategories?slug=${slug}${sortString}`,
        this._headers,
        'GET',
      );

      const categoryId = categoryData?.items?.[0]?.categoryId;
      if (!categoryId) {
        logError(`No category ID found for slug: ${slug}`);
        return { isError: true, message: `No category ID found for slug: ${slug}`, data: null };
      }

      // Fetch Products using categoryId
      return await fetchData(
        `${this._host}/action/product/query?categories[0]=${categoryId}&limit=${limit}${sortString}`,
        this._headers,
        'GET',
      );
    } catch (error) {
      logError(`Error fetching category products for slug ${slug}`, error);
      return {
        isError: true,
        message: `Error fetching category products for slug ${slug}`,
        data: null
      };
    }
  }

  /**
   * Fetches trade-in products.
   */
  async handleGetTradeInProducts(limit = 99, sortString = ''): Promise<any> {
    try {
      const query = `productFilters[filters][0][type]=boolean&productFilters[filters][0][field]=variants.attributes.tradeInFlag&productFilters[values][variants.attributes.tradeInFlag]=true&limit=${limit}${sortString}`;
      return fetchData(`${this._host}/action/product/query?${query}`, this._headers);
    } catch (error) {
      logError('Error fetching trade-in products', error);
      return {
        isError: true,
        message: 'Error fetching trade-in products',
        data: null
      };
    }
  }
}
